import { MetaFunction } from "@remix-run/node"
import {
  articleBodyStyle,
  articleImageStyle,
  bold,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  infoWrap,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import StickyHeader from "~/components/StickyHeader"
import Footer from "~/components/Footer"
import LinkButton from "~/components/LinkButton"
import Logo from "~/components/Logo"
import { page } from "~/shared/styles.css"

export const meta: MetaFunction = () => {
  return [
    {
      title: "The Future of Resilient Gardening | Adapting to Climate Change",
    },
    {
      name: "description",
      content:
        "Discover how resilient planting, smart design and biodiversity can help gardens thrive in a changing climate. Explore the future of climate-ready gardening.",
    },
    {
      property: "og:title",
      content: "Gardening While Adapting to Climate Change",
    },
    {
      property: "og:description",
      content:
        "Discover how resilient planting, smart design and biodiversity can help gardens thrive in a changing climate. Explore the future of climate-ready gardening.",
    },
    // {
    //   property: "og:image",
    //   content:
    //     "https://rescapeai.com/how-to-design-a-contemporary-garden/third-ai-garden-design-with-stone-paving-area.webp",
    // },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/article/gardening-while-adapting-to-climate-change",
    },
    // {
    //   "script:ld+json": {
    //     "@context": "https://schema.org",
    //     "@type": "Article",
    //     headline: "Features of Contemporary Gardens | Modern Garden Design Essentials",
    //     description:
    //       "Discover the key features of contemporary gardens, including minimalist layouts, modern materials, sustainable elements, and outdoor living spaces. Learn what defines modern garden design.",
    //     author: {
    //       "@type": "Organization",
    //       name: "Rescape AI",
    //     },
    //     datePublished: "2025-10-22",
    //     dateModified: "2025-10-22",
    //     publisher: {
    //       "@type": "Organization",
    //       name: "Rescape AI",
    //       logo: {
    //         "@type": "ImageObject",
    //         url: "https://rescapeai.com/logo.webp",
    //       },
    //     },
    //     image:
    //       "https://rescapeai.com/how-to-design-a-contemporary-garden/third-ai-garden-design-with-stone-paving-area.webp",
    //     mainEntityOfPage: {
    //       "@type": "WebPage",
    //       "@id": "https://rescapeai.com/article/features-of-contemporary-gardens",
    //     },
    //     articleSection: ["Exploring Key Elements of Contemporary Garden Design"],
    //   },
    // },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/articles/holm-oak.webp)",
          backgroundPosition: "20%",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>The Future of Resilient Gardens</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>
            How climate change is reshaping planting, design, and the beauty of tomorrow’s
            gardens
          </h2>
          <p className={articleBodyStyle}>
            As our climate shifts and seasons stretch into unfamiliar rhythms, our gardens
            stand at the threshold of change. Rising temperatures, wetter winters, drier
            summers and more intense storms are reshaping the landscapes we know. Yet,
            rather than signalling an end to traditional gardening joy, this
            transformation marks the beginning of a new chapter — one where resilience
            becomes the beating heart of the future garden. Gardens have always evolved,
            and today they are poised to become more adaptable, diverse and
            life-sustaining than ever before.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              A new rhythm of the seasons
            </h3>
            <p className={articleBodyStyle}>
              Longer, milder springs and autumns are already extending the growing season,
              encouraging earlier blooms and slower leaf fall. Tasks such as mowing and
              weeding now stretch deeper into the year. Extreme weather has become less
              predictable — dry spells linger, followed by sudden heavy downpours,
              particularly in winter. Some regions, especially East Anglia, may resemble
              drier Mediterranean landscapes, while the West and North prepare for wetter
              winters and stronger storms.
            </p>
            <figure>
              <img
                src="/articles/pouring-rain.webp"
                className={articleImageStyle}
                alt="Puddles grow with pouring rain"
              />
              <figcaption className={figCaptionStyle}>
                Heavy rain causing puddles in a garden
              </figcaption>
            </figure>
            <p className={articleBodyStyle}>
              Rather than resisting these changes, resilient gardens work with them —
              embracing flexibility, selecting plants adapted to shifting conditions, and
              designing landscapes that absorb adversity and thrive.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Plants that will shape tomorrow’s gardens
            </h3>
            <p className={articleBodyStyle}>
              Resilient gardens celebrate plants that can endure, adapt and flourish. As
              the report suggests, warmer conditions allow a broader palette of plants to
              thrive, particularly those familiar to temperate and Mediterranean climates.
            </p>
            <p className={articleBodyStyle}>
              <strong className={bold}>In drought-prone areas</strong>, evergreen
              survivors such as Arbutus unedo (strawberry tree), Garrya elliptica, holm
              oak and hardy palms have already found popularity, outlasting thirsty
              counterparts. Species roses and tough climbers like Rosa ‘Veilchenblau’ and
              ‘American Pillar’ excel where hybrid teas falter in increasingly dry soils.
            </p>
            <p className={articleBodyStyle}>
              <strong className={bold}>In warmer wetter regions</strong>, camellias,
              escallonias and fuchsias are becoming mainstay hedge plants, while acacias,
              Chilean fire bushes and eucalyptus groves add striking new silhouettes to
              the skyline.
            </p>
            <figure>
              <img
                src="/articles/fuchsia.webp"
                className={articleImageStyle}
                alt="Closeup of pink and purple Fuchsias"
                style={{ marginTop: "1rem" }}
              />
              <figcaption className={figCaptionStyle}>
                Closeup of pink and purple Fuchsias
              </figcaption>
            </figure>
            <p className={articleBodyStyle}>
              <strong className={bold}>
                In areas subject to storms and fluctuating conditions
              </strong>
              , gardeners are turning to robust, multi-purpose shrubs such as Amelanchier,
              Cotinus, Sambucus, Philadelphus and climbers like Clematis montana and
              honeysuckle, valued for their tolerance and wildlife appeal.
            </p>
            <p className={articleBodyStyle}>
              A resilient garden does not abandon beauty — it simply redefines it through
              endurance.
            </p>
          </div>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Designing with resilience in mind</h3>
          <p className={articleBodyStyle}>
            The garden of the future is shaped not only by plants but by thoughtful design
            that embraces extremes rather than fighting them.
          </p>
          <ul className={unorderedList}>
            <li>
              Rain gardens and bioswales help manage winter flooding by slowing and
              absorbing water.
            </li>
            <li>
              Raised beds lift plant roots clear of waterlogged soils while aiding summer
              irrigation.
            </li>
            <li>
              Dry meadows replace thirsty lawns in areas where watering becomes
              unsustainable, naturalised with drought-tolerant perennials and bulbs.
            </li>
            <li>
              Shade structures and canopy trees create cool refuges during hotter summers
              and protect delicate foliage from scorch.
            </li>
            <li>
              Water storage systems, from small butts to subterranean tanks, capture
              intense rainfall for later use.
            </li>
            <li>
              Green roofs and walls help insulate homes and reduce runoff, while
              increasing habitat and cooling air.
            </li>
          </ul>
          <p className={articleBodyStyle}>
            Here, resilience becomes functional elegance.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Biodiversity as a form of strength</h3>
          <p className={articleBodyStyle}>
            Climate change risks unsettling long-held synchronies between flowering times
            and pollinator lifecycles. When conditions accelerate blooms but insects
            emerge at their usual pace, a mismatch can occur.
          </p>
          <figure style={{ marginBottom: "1rem" }}>
            <img
              src="/articles/bee-collecting-pollen.webp"
              className={articleImageStyle}
              alt="Closeup of a bee harvesting pollen"
              style={{ marginTop: "1rem" }}
            />
            <figcaption className={figCaptionStyle}>
              Closeup of a bee harvesting pollen
            </figcaption>
          </figure>
          <p className={articleBodyStyle}>
            To foster resilience, gardeners are increasingly planting a broad spectrum of
            nectar sources — including both native and non-native species — to extend
            feeding seasons and support a wider variety of pollinators. Research shows
            these diverse plantings can significantly bolster ecological health.
          </p>
          <p className={articleBodyStyle}>
            Similarly, a watchful eye on pests and diseases — which are likely to expand
            their range in warmer, damper conditions — will ensure issues are managed
            early, favouring low-impact solutions such as pruning and biological controls.
          </p>
          <p className={articleBodyStyle}>
            In resilient gardens, biodiversity isn’t just welcomed — it is a strategy for
            survival.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>Gardeners as climate stewards</h3>
          <p className={articleBodyStyle}>
            More than half of UK adults engage in gardening, giving this community a
            powerful role in climate resilience.
          </p>
          <p className={articleBodyStyle}>
            By adopting peat-free composts, reducing petrol-powered tools, planting more
            trees and shrubs for carbon storage, composting waste, and choosing products
            with a lower environmental footprint, gardeners can help shape a greener
            future even beyond their own gates.
          </p>
          <p className={articleBodyStyle}>
            Each garden, no matter how small, becomes part of a national network of
            climate-adaptive green spaces — cooling urban areas, preventing flooding,
            filtering pollutants and nurturing mental and physical wellbeing.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>A future built on resilience — and hope</h3>
          <p className={articleBodyStyle}>
            The gardens of tomorrow will not look exactly like those of the past — but
            they will be no less full of beauty, life and meaning. They will hum with
            pollinators, sway with drought-tolerant grasses, shimmer with moisture-loving
            ferns in shaded corners and flourish in every region with the right planting.
            They will not merely survive change — they will express it with grace.
          </p>
          <p className={articleBodyStyle}>
            Resilience in gardening is not just about withstanding challenge; it is about
            embracing transformation with creativity and care. In adapting our planting,
            our designs and our habits, we are not losing something — we are growing
            towards something new.
          </p>
          <p className={articleBodyStyle}>
            And in doing so, we are ensuring that gardens — as places of refuge,
            inspiration and connection — continue to thrive long into the future.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h3Style}>References</h3>
          <ul className={unorderedList}>
            <li>
              <a href="https://www.rhs.org.uk/science/pdf/rhs-gardening-in-a-changing-climate-report-summary.pdf">
                Gardening in a Changing Climate Report - Summary Document
              </a>
            </li>
            <li>
              <a href="https://www.rhs.org.uk/garden-inspiration/plants-we-love/future-of-roses">
                The RHS - What is the future of Roses?
              </a>
            </li>
          </ul>
        </section>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <p className={h2Style} style={{ maxWidth: "50%", textAlign: "center" }}>
            Use AI to redesign your garden with just a photo. Get started below.
          </p>
          <img
            className={articleImageStyle}
            src="/guide/garden-5.2-after-ai-contemporary-design.webp"
            style={{ marginTop: "2rem" }}
          />
          <LinkButton url="/hello/signup">Design now</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
